// src/components/TransactionNotification/TransactionNotification.js
import React, { useState, useEffect, useRef } from 'react';
import { FiCheckCircle, FiLoader, FiExternalLink, FiX, FiClock } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import './TransactionNotification.css';

/**
 * TransactionNotification Component
 * 
 * Displays a floating notification for transaction status when auto-confirmation is enabled.
 * Shows a spinner during processing, and confirmation details on success.
 * Auto-dismisses after success is shown for 5 seconds.
 * 
 * @param {boolean} isVisible - Controls visibility of the notification
 * @param {string} status - Current status: 'processing' or 'success'
 * @param {string} txnHash - Transaction hash (for success state)
 * @param {string} explorerUrl - Block explorer URL for the current chain
 * @param {function} onClose - Callback when notification is closed
 */
const TransactionNotification = ({ 
  isVisible, 
  status, 
  txnHash, 
  explorerUrl,
  onClose 
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(status);
  const [progress, setProgress] = useState(0);
  const dismissTimerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const { t } = useTranslation();
  
  // Update internal status when prop changes
  useEffect(() => {
    if (status !== currentStatus) {
      console.log(`TransactionNotification: Status changed from ${currentStatus} to ${status}`);
      setCurrentStatus(status);
      
      // Reset progress bar when changing to success
      if (status === 'success') {
        setProgress(0);
      }
    }
  }, [status, currentStatus]);

  // Clear timers on unmount
  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, []);
  
  // Handle visibility changes
  useEffect(() => {
    // When hiding, reset closing state
    if (!isVisible) {
      setIsClosing(false);
    }
  }, [isVisible]);
  
  // Auto-close notification after success with progress bar
  useEffect(() => {
    // Clear any existing timers
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    
    if (currentStatus === 'success' && isVisible && !isClosing) {
      console.log("Success state detected, scheduling auto-close");
      
      // Progress bar animation (5 seconds)
      const duration = 5000; // 5 seconds
      const intervalTime = 50; // Update every 50ms
      const increment = (intervalTime / duration) * 100;
      
      progressIntervalRef.current = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + increment;
          return newProgress >= 100 ? 100 : newProgress;
        });
      }, intervalTime);
      
      // Close after duration
      dismissTimerRef.current = setTimeout(() => {
        handleClose();
      }, duration);
    }
    
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, [currentStatus, isVisible, isClosing]);
  
  // Handle close button click
  const handleClose = () => {
    if (isClosing) return; // Prevent multiple triggers
    
    console.log("Starting close animation");
    setIsClosing(true);
    
    // Clear any running timers
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    
    // Wait for animation to complete
    setTimeout(() => {
      if (onClose) {
        console.log("Animation complete, calling onClose");
        onClose();
      }
      setIsClosing(false);
      setProgress(0);
    }, 400); // Match CSS transition duration
  };
  
  if (!isVisible && !isClosing) return null;
  
  // Format transaction hash for display
  const formattedHash = txnHash ? `${txnHash.slice(0, 8)}...${txnHash.slice(-6)}` : '';
  
  return (
    <div 
      className={`transaction-notification ${isClosing ? '' : 'visible'} ${currentStatus === 'success' ? 'success-state' : 'processing-state'}`}
    >
      <button 
        className="notification-close-btn"
        onClick={handleClose}
        aria-label={t('actions.close')}
      >
        <FiX />
      </button>
      
      <div className="notification-status-icon">
        {currentStatus === 'processing' && (
          <div className="spinner-container">
            <FiLoader className="notification-icon spinning" />
          </div>
        )}
        
        {currentStatus === 'success' && (
          <div className="success-icon-container">
            <FiCheckCircle className="notification-icon success" />
          </div>
        )}
      </div>
      
      <div className="notification-content">
        <div className="notification-header">
          {currentStatus === 'processing' && (
            <div className="notification-title">
              {t('transactions.notification.processing')}
            </div>
          )}
          
          {currentStatus === 'success' && (
            <div className="notification-title success">
              {t('transactions.notification.confirmed')}
            </div>
          )}
        </div>
        
        <div className="notification-details">
          {currentStatus === 'processing' && (
            <div className="processing-message">
              <FiClock className="time-icon" />
              <span>{t('transactions.notification.wait')}</span>
            </div>
          )}
          
          {currentStatus === 'success' && txnHash && (
            <>
              <div className="notification-hash">{formattedHash}</div>
              <a 
                href={`${explorerUrl}/tx/${txnHash}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="notification-link"
              >
                <FiExternalLink className="link-icon" /> {t('transactions.viewOnExplorer')}
              </a>
            </>
          )}
        </div>
      </div>
      
      {/* Auto-dismiss progress bar (only shown for success state) */}
      {currentStatus === 'success' && (
        <div className="progress-container">
          <div 
            className="progress-bar" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      )}
    </div>
  );
};

export default TransactionNotification;